import React, { useEffect, useRef, useState } from 'react';
import { Palette } from 'lucide-react';
import {
  GROUP_TIMELINE_COLOR_MODES,
  GROUP_TIMELINE_COLOR_MODE_LABELS,
  type GroupTimelineColorMode,
  type GroupTimelineLegendItem,
} from '../../utils/groupTimeline';
import './GroupTimelineColorControls.css';

interface GroupTimelineColorControlsProps {
  mode: GroupTimelineColorMode;
  onChange: (mode: GroupTimelineColorMode) => void;
  legend: GroupTimelineLegendItem[];
}

const GroupTimelineColorControls: React.FC<GroupTimelineColorControlsProps> = ({
  mode,
  onChange,
  legend,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="pm-hub-color-controls" ref={rootRef}>
      <button
        type="button"
        className={`pm-hub-color-trigger${open ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Color bars by ${GROUP_TIMELINE_COLOR_MODE_LABELS[mode]}`}
        title="Color bars by project, supplier, status, department, priority, or phase"
        onClick={() => setOpen((v) => !v)}
      >
        <Palette size={13} strokeWidth={2} aria-hidden />
        <span>Color: {GROUP_TIMELINE_COLOR_MODE_LABELS[mode]}</span>
      </button>
      {open && (
        <div className="pm-dropdown pm-hub-color-menu" role="listbox" aria-label="Color bars by">
          {GROUP_TIMELINE_COLOR_MODES.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={mode === option}
              className={`pm-dropdown-item${mode === option ? ' is-active' : ''}`}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              {GROUP_TIMELINE_COLOR_MODE_LABELS[option]}
            </button>
          ))}
        </div>
      )}
      {legend.length > 0 && (
        <ul className="pm-hub-color-legend" aria-label="Bar color legend">
          {legend.map((item) => (
            <li key={item.key} className="pm-hub-color-legend-item">
              <span className="pm-hub-color-legend-swatch" style={{ background: item.color }} aria-hidden />
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GroupTimelineColorControls;
